import { Box, CircularProgress, Switch, Typography } from '@material-ui/core'
import { ExportPickingList } from 'components/domain/csv/ExportPickingList'
import { Button, CustomTooltip } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import {
  changeArticleProp,
  editUserAttribute /*, forceClosePickingList, getUserPickingLists */,
} from 'lib/api'
import { useDialog, useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import { useFetchPickingList as useFetchPickingList } from 'lib/hooks/useFetchPickingList'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ARTICLE_STATES,
  ChangeArticlePropsWrapper,
  getAvailableQuantityNetOfPickedAndWithIssues,
  getDisplayLocationName,
  getPlaysetCoefficient,
  Location,
  Order,
  OrderArticle,
  OrderResponseDto,
} from 'shared'
import { ReportIssueDialog } from '../ReportIssueDialog'
import { ArticleSorter } from './ArticleSorter'
import { LocationsDialog } from './LocationsDialog'
import { LocationsErrorDialog } from './LocationsErrorDialog'
import { PickingImageDisplay } from './PickingImageDisplay'
import { PickingTable } from './PickingTable'
import { SingleImagePicking } from './SingleImagePicking'

export interface PickingPresenterProps {
  handleUpdateOrders: (updatedOrders: Order[]) => void
}

// The quantity to pick lives on the row's own locationInfo, but the availability has to be read from
// the article's locations set: that one is replaced with the backend's after every pick and back to
// pick (see updateReservedQuantityInBothListsAccordingLastOrderChanges), while locationInfo keeps the
// counters it was loaded with. Reading it here would show a residual quantity that contradicts the
// locations dialog as soon as another row of the same article is picked.
const getLocationWithFreshQuantities = (article: OrderArticle) => {
  const locationInfo = article.locationInfo!

  return article.locations?.find((location) => location.name === locationInfo.name) ?? locationInfo
}

// The availability is only shown to the users who ask for it through the
// showAvailableQuantityInPickingLocations setting: it is what is left in the whole location, shared
// with the other orders being picked, not what this order alone can take from it.
export const getQuantityToPickLabel = (article: OrderArticle, showAvailableQuantity: boolean) => {
  const quantityToPick = Math.abs(article.locationInfo?.deltaQuantity ?? 0)
  if (!showAvailableQuantity) return `${quantityToPick}`

  const availableQuantity = getAvailableQuantityNetOfPickedAndWithIssues(
    getLocationWithFreshQuantities(article)
  )

  return `${quantityToPick} (${availableQuantity} available)`
}

// A picked row only says how much left the location, no total: the availability is something the rows
// still to pick need. Math.abs is not cosmetic here, the two lists disagree on the sign — a row picked
// in this session keeps the positive quantity to pick it carried in the other list, a row loaded as
// already picked arrives negative from the backend — so without it the same row reads 3, then -3 after
// a reload.
export const getPickedQuantityLabel = (article: OrderArticle) =>
  `${Math.abs(article.locationInfo?.deltaQuantity ?? 0)}`

export const locationBeforePick = (article: OrderArticle, showAvailableQuantity: boolean) => {
  const location = article.locationInfo

  if (!location) {
    return (
      <span className="picking-location-name" title="Missing location. Item not found in inventory">
        Missing location. Item not found in inventory
      </span>
    )
  } else {
    return (
      <div className="d-flex flex-row">
        <span className="me-1 picking-location-name" title={getDisplayLocationName(location.name)}>
          {getDisplayLocationName(location.name)}:{' '}
        </span>
        <span className="flex-shrink-0">
          {getQuantityToPickLabel(article, showAvailableQuantity)}
        </span>
      </div>
    )
  }
}

export const PickingPresenter = ({ handleUpdateOrders }: PickingPresenterProps): JSX.Element => {
  const { user, setUser } = useUser()
  const activatedSmartLocation = !!user.activeSmartLocations
  const { t } = useTranslation()
  // const { setUserPickingLists } = useOrderStore() // only used by the removed "Close list" flow
  const [setExportDialogOpen, ExportDialog] = useDialog()

  const {
    activePickingList,
    selectedArticle,
    articlesToPick,
    articlesPicked,
    setActivePickingList,
    setSelectedArticle,
    setArticlesToPick,
    setArticlesPicked,
    setLastPicked,
    setDisablePickingButtons,
    setRefetchArticlesToPick,
  } = useOrderStore()

  const { callGetArticlesToPick, loading, getMapOfOrderArticlesByPtOrderArticleId } =
    useFetchPickingList()

  const [openReportIssueDialog, setOpenReportIssueDialog] = useState<boolean>(false)
  const { convertOrderResponseDtoToOrderClass } = useFetchOrders()

  // const gradingAlertShownRef = useRef(false)
  const [view, setView] = useState<string>('list')
  const [currentArticleIdx, setCurrentArticleIdx] = useState<number>(0)
  const [openLocationsDialog, setOpenLocationsDialog] = useState(false)
  const [openLocationDialogInReadOnlyMode, setOpenLocationDialogInReadOnlyMode] = useState(false)
  const [openLocationsErrorDialog, setOpenLocationsErrorDialog] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const smartLocationsTooltipTitle = t('orders.picking.smartLocations')

  /**
   * used in locations' dialog to calculate amount to pick
   */
  const [currentOrderToPick, setCurrentOrderToPick] = useState<{
    article: OrderArticle | undefined
    quantityToPickInOrder: number | undefined
  }>({ article: undefined, quantityToPickInOrder: undefined })
  setCurrentArticleIdx
  setOpenLocationDialogInReadOnlyMode

  // Initial fetch on picking list selection (no sorting args -> backend returns the persisted sorting_fields)
  useEffect(() => {
    if (activePickingList && activePickingList.state === 'open') {
      callGetArticlesToPick()
    }
  }, [activePickingList?.ptId])

  useEffect(() => {
    setRefetchArticlesToPick((newSortingFields: string[]) => {
      if (activePickingList && activePickingList.state === 'open') {
        return callGetArticlesToPick(newSortingFields)
      }
      return Promise.resolve()
    })
    return () => setRefetchArticlesToPick(null)
  }, [activePickingList?.ptId])

  const handleChangeSmartLocation = async () => {
    const updatedUser = await editUserAttribute(!activatedSmartLocation, 'activeSmartLocations')
    setUser({ ...user, ...updatedUser })
  }

  const handleChooseAnotherList = () => {
    setActivePickingList(undefined)
  }

  const openExportOrdersDialog = () => {
    setExportDialogOpen(true)
  }

  /**
   *
   * updates the pickedQs of a list by first transforming it into a map, then iterating only on the items needed,
   * and finally transforming it back into a list. with this function I update pickedQty only for a specific ptOrderArticle
   */
  const updatePickedQuantityInDestinationList = (
    toPick: OrderArticle[],
    picked: OrderArticle[],
    pickedQuantity: number,
    ptOrderArticleId: number
  ) => {
    const mapToPick = getMapOfOrderArticlesByPtOrderArticleId(toPick) // Map<number, OrderArticle[]>
    const mapPicked = getMapOfOrderArticlesByPtOrderArticleId(picked) // Map<number, OrderArticle[]>

    const findToPick = mapToPick.get(ptOrderArticleId)
    const findPicked = mapPicked.get(ptOrderArticleId)

    if (findToPick) {
      findToPick.forEach((articleToPick) => {
        articleToPick.pickedQuantity = pickedQuantity
      })
    }

    if (findPicked) {
      findPicked.forEach((articlePicked) => {
        articlePicked.pickedQuantity = pickedQuantity
      })
    }

    // The maps are only there to carry the quantity onto every row of an article, and they hold the
    // very same instances as the two lists. Rebuilding the lists out of them would emit each article
    // as a block: a row just picked would jump next to the one picked earlier instead of staying at
    // the bottom, and the rows still to pick would lose the order Rails sorted them in.
    return [
      toPick.map((article) => new OrderArticle(article)),
      picked.map((article) => new OrderArticle(article)),
    ]
  }

  const groupByPtArticleId = (list: OrderArticle[]) => {
    const map = new Map<number, OrderArticle[]>()
    for (const a of list) {
      const key = a.ptArticleId ?? -1
      const bucket = map.get(key)
      if (bucket) bucket.push(a)
      else map.set(key, [a])
    }
    return map
  }

  /**
   * when I pick or back-to-pick, the backend responds with the updated order, which also contains the list of order articles in it.
   * this function checks the locations array to know, for each location, how much has been picked and how much has issues, so that
   * for non-smart locations we know the real available quantity.
   */
  const updateReservedQuantityInBothListsAccordingLastOrderChanges = (
    fetchedLocations: Location[],
    isBackToPick: boolean,
    toPick: OrderArticle[],
    picked: OrderArticle[],
    ptArticleId: number
  ) => {
    isBackToPick

    const mapToPick = groupByPtArticleId(toPick)
    const mapPicked = groupByPtArticleId(picked)

    const findToPick = mapToPick.get(ptArticleId) ?? []
    const findPicked = mapPicked.get(ptArticleId) ?? []

    // WARNING: shared references are INTENTIONAL. All order articles with the same ptArticleId
    // point to the same `fetchedLocations` set (shared physical stock), so they show the same
    // BE-updated availability. Do NOT mutate `.locations` elements in place: the change would
    // silently bleed to all siblings (potential post-deploy bug). The pick deltaQuantity belongs
    // on `locationInfo` or on clones (see LocationsDialog), never here.
    if (findToPick.length > 0) {
      findToPick.forEach((articleToPick: OrderArticle) => {
        articleToPick.locations = [...fetchedLocations]
      })
    }

    if (findPicked.length > 0) {
      findPicked.forEach((articlePicked: OrderArticle) => {
        articlePicked.locations = [...fetchedLocations]
      })
    }

    // The groups hold the very same instances as the two lists, so the locations set above is
    // already in place. Rebuilding the lists out of the maps would emit each ptArticle as a block,
    // pulling rows of different orders next to each other and losing both the picking list sort and
    // the order the user picked in.
    return [[...toPick], [...picked]]
  }

  const isUnknownArticle = (article: OrderArticle) => {
    if (!article.locations || article.locations.length === 0) {
      return true
    }
    return false
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////

  const handlePick = (article: OrderArticle) => {
    setLastPicked(article)
  }

  const pickUnknownPtArticle = async (article: OrderArticle) => {
    setDisablePickingButtons(true)
    const changes: ChangeArticlePropsWrapper = {
      picking: {
        ptOrderArticleId: article.ptOrderArticleId!,
        mkmProductId: article.idProduct,
        orderId: article.ptOrderId,
        pickedQuantity: article.quantity,
        ptArticleId: -1,
        cardmarketOrderArticleId: article.ptArticleId!,
      },
      maxPickingQuantity: article.quantity,
    }

    try {
      const response = await changeArticleProp(
        article.ptOrderId,
        article.ptOrderArticleId!,
        changes
      )
      handleUpdateOrders([convertOrderResponseDtoToOrderClass(response)])
      const copyOfToPick = _.cloneDeep(articlesToPick)
      const foundPickedIdx = copyOfToPick.findIndex((picked) => {
        return picked.ptOrderArticleId! === article.ptOrderArticleId!
      })
      copyOfToPick.splice(foundPickedIdx, 1)

      const copyOfOPicked = _.cloneDeep(articlesPicked)
      article.articleState = ARTICLE_STATES.PICKED
      copyOfOPicked.push(article)

      setArticlesPicked(copyOfOPicked)
      setArticlesToPick(copyOfToPick)
      setDisablePickingButtons(false)
    } catch (e) {
      setDisablePickingButtons(false)
      return enqueueSnackbar(t('orders.picking.pickInvalidOrderStatus'), { variant: 'error' })
    }
  }

  /**
   * Values ​​the deltaQ in the item that has been selected, or opens the LocationsDialog
   */
  const handleCompletePicking = (article: OrderArticle, readonly: boolean) => {
    article.updatedAt = new Date()
    const quantityToPickInOrder = article.isPlayset
      ? article.quantity * getPlaysetCoefficient(article.card.idGame!)
      : article.quantity

    // const availableQuantityInInventory = article.locations?.reduce(
    //   (acc, loc) => acc + (loc.quantity - loc.reservedQuantity),
    //   0
    // )

    if (readonly) {
      setOpenLocationDialogInReadOnlyMode(true)
      setOpenLocationsDialog(true)
      setCurrentOrderToPick({ article, quantityToPickInOrder })
    } else {
      // picking for unknown ptArticles
      if (isUnknownArticle(article)) {
        pickUnknownPtArticle(article)
        return
      }

      if (activatedSmartLocation) {
        article.pickedQuantity += Math.abs(article.locationInfo?.deltaQuantity ?? 0)
        article.articleState = ARTICLE_STATES.PICKED
        handleChoosePickingLocation(article)
      } else {
        setOpenLocationDialogInReadOnlyMode(readonly)
        setOpenLocationsDialog(true)
        setCurrentOrderToPick({ article, quantityToPickInOrder })
      }
    }
  }

  const whenNotUsingSmartLocationsReCalculateBothListWithUserChoise = (
    pickedArticle: OrderArticle,
    chosenLocations: Location[],
    toPick: OrderArticle[],
    picked: OrderArticle[]
  ) => {
    const copyOfToPick = _.cloneDeep(toPick)
    const copyOfPicked = _.cloneDeep(picked)

    const id = pickedArticle.ptOrderArticleId

    // Drop every row of the article just picked, leaving the others where they are: grouping them
    // by article to rebuild the list would emit each one as a block and lose the picking list sort.
    const newArticlesToPick =
      id == null ? copyOfToPick : copyOfToPick.filter((article) => article.ptOrderArticleId !== id)

    const quantityToPickInOrder = pickedArticle.isPlayset
      ? pickedArticle.quantity * getPlaysetCoefficient(pickedArticle.card.idGame!)
      : pickedArticle.quantity

    for (const location of chosenLocations) {
      if (!location.deltaQuantity || location.deltaQuantity === 0) continue
      const newOrderArticle: OrderArticle = new OrderArticle({
        ...pickedArticle,
        pickedQuantity: quantityToPickInOrder,
        locationInfo: _.cloneDeep(location),
        locations: _.cloneDeep(chosenLocations),
        articleState: ARTICLE_STATES.PICKED,
      })
      copyOfPicked.push(newOrderArticle)
    }

    return [newArticlesToPick, copyOfPicked]
  }

  /**
   * It makes the actual call to BE to communicate the user's choice.
   * At the same time it also updates the entire database used in FE
   */
  const handleChoosePickingLocation = async (article: OrderArticle) => {
    setDisablePickingButtons(true)
    const maxPickingQuantity = article.isPlayset
      ? article.quantity * getPlaysetCoefficient(article.card.idGame!)
      : article.quantity

    let orderResponseFromChangeArticleProp: OrderResponseDto | undefined

    if (activatedSmartLocation) {
      const changes: ChangeArticlePropsWrapper = {
        picking: {
          ptOrderArticleId: article.ptOrderArticleId!,
          location: article.locationInfo,
          mkmProductId: article.idProduct,
          orderId: article.ptOrderId,
          pickedQuantity: Math.abs(article.locationInfo?.deltaQuantity ?? 0),
          ptArticleId: article.ptArticleId!,
          cardmarketOrderArticleId: article.ptArticleId!,
        },
        maxPickingQuantity: maxPickingQuantity,
      }

      try {
        orderResponseFromChangeArticleProp = await changeArticleProp(
          article.ptOrderId,
          article.ptOrderArticleId!,
          changes
        )

        handleUpdateOrders([
          convertOrderResponseDtoToOrderClass(orderResponseFromChangeArticleProp),
        ])
      } catch {
        setDisablePickingButtons(false)
        if (article.locationInfo?.deltaQuantity === 0) {
          return enqueueSnackbar(t('orders.picking.invalidQuantity'), { variant: 'error' })
        }
        return enqueueSnackbar(t('orders.picking.pickInvalidOrderStatus'), { variant: 'error' })
      }

      // I update the two lists by moving the article from one list to the other
      // const copyOfToPick = _.cloneDeep(articlesToPick)
      const idx = articlesToPick.findIndex(
        (a) =>
          a.ptOrderArticleId! === article!.ptOrderArticleId! &&
          a.locationInfo?.name === article.locationInfo?.name
      )

      if (idx !== -1) {
        articlesToPick.splice(idx, 1)
      }
      // const copyOfPicked = _.cloneDeep(articlesPicked)
      articlesPicked.push(article)

      // I update the pickedQs in both lists
      const [listOfArticlesToPick, listOfPickedArticles] = updatePickedQuantityInDestinationList(
        articlesToPick,
        articlesPicked,
        article.pickedQuantity,
        article.ptOrderArticleId!
      )

      // The pick has just consumed part of the location, so the other rows of the same pt article
      // have to take the availability the backend reports now: the manual flow and back to picking
      // already do it, and without it here the rows keep offering the quantity the location had when
      // the picking list was loaded.
      const updatedArticle = orderResponseFromChangeArticleProp.articles.find(
        (searchingArticle: any) => {
          return searchingArticle.ptOrderArticleId === article.ptOrderArticleId!
        }
      )

      const [refreshedArticlesToPick, refreshedPickedArticles] = updatedArticle
        ? updateReservedQuantityInBothListsAccordingLastOrderChanges(
            updatedArticle.locations,
            false,
            listOfArticlesToPick,
            listOfPickedArticles,
            article.ptArticleId!
          )
        : [listOfArticlesToPick, listOfPickedArticles]

      setArticlesToPick(refreshedArticlesToPick)
      setArticlesPicked(refreshedPickedArticles)
    } else {
      // NOT SMART LOCATIONS
      for (const location of article.locations) {
        if (location.deltaQuantity === 0) continue
        const changes: ChangeArticlePropsWrapper = {
          picking: {
            ptOrderArticleId: article.ptOrderArticleId!,
            location: location,
            mkmProductId: article.idProduct,
            orderId: article.ptOrderId,
            pickedQuantity: Math.abs(article.locationInfo?.deltaQuantity ?? 0),
            ptArticleId: article.ptArticleId!,
            cardmarketOrderArticleId: article.ptArticleId!,
          },
          maxPickingQuantity: maxPickingQuantity,
        }

        try {
          orderResponseFromChangeArticleProp = await changeArticleProp(
            article.ptOrderId,
            article.ptOrderArticleId!,
            changes
          )
          handleUpdateOrders([
            convertOrderResponseDtoToOrderClass(orderResponseFromChangeArticleProp),
          ])
        } catch {
          setDisablePickingButtons(false)
          if (article.locationInfo?.deltaQuantity === 0) {
            return enqueueSnackbar(t('orders.picking.invalidQuantity'), { variant: 'error' })
          }
          return enqueueSnackbar(t('orders.picking.pickInvalidOrderStatus'), { variant: 'error' })
        }
      }

      // first fix the picked quantities: I'm reading the locations returned when the dialog closed, so the array already holds the deltas I need
      const [toPick, picked] = whenNotUsingSmartLocationsReCalculateBothListWithUserChoise(
        article,
        article.locations,
        articlesToPick,
        articlesPicked
      )

      // then, based on the latest response from changeArticleProp, fix the locations array across all ptArticleIds
      if (!orderResponseFromChangeArticleProp) {
        setDisablePickingButtons(false)
        return
      }
      const updatedArticle = orderResponseFromChangeArticleProp.articles.find(
        (searchingArticle: any) => {
          return searchingArticle.ptOrderArticleId === article.ptOrderArticleId!
        }
      )
      if (!updatedArticle) {
        setDisablePickingButtons(false)
        return
      }
      const updatedLocations = updatedArticle.locations

      const [newArticlesToPick, newPickedArticles] =
        updateReservedQuantityInBothListsAccordingLastOrderChanges(
          updatedLocations,
          true,
          toPick,
          picked,
          article.ptArticleId!
        )

      setArticlesToPick(newArticlesToPick)
      setArticlesPicked(newPickedArticles)
    }
    setDisablePickingButtons(false)
  }

  const backToPickingUnknownPtArticle = async (article: OrderArticle) => {
    const changes: ChangeArticlePropsWrapper = {
      toPick: {
        ptOrderArticleId: article.ptOrderArticleId!,
        mkmProductId: article.idProduct,
        orderId: article.ptOrderId,
        ptArticleId: -1,
        location: article.locationInfo,
        cardmarketOrderArticleId: article.ptArticleId!,
      },
      maxPickingQuantity: article.isPlayset
        ? article.quantity * getPlaysetCoefficient(article.card.idGame!)
        : article.quantity,
    }

    await changeArticleProp(article.ptOrderId, article.ptOrderArticleId!, changes)

    const copyOfOPicked = _.cloneDeep(articlesPicked)
    const foundPickedIdx = copyOfOPicked.findIndex((picked) => {
      return picked.ptOrderArticleId! === article.ptOrderArticleId!
    })
    copyOfOPicked.splice(foundPickedIdx, 1)

    const copyOfToPick = _.cloneDeep(articlesToPick)
    article.articleState = ARTICLE_STATES.TO_PICK
    copyOfToPick.push(article)

    setArticlesPicked(copyOfOPicked)
    setArticlesToPick(copyOfToPick)
  }

  // On unpick, put the row back at its sorted position (Rails sends sort_index per row) instead of
  // at the bottom. Rows without an index (Unknown/synthetic locations) keep sinking to the bottom.
  const insertToPickBySortIndex = (list: OrderArticle[], article: OrderArticle) => {
    if (article.sortIndex === undefined || article.sortIndex === null) {
      list.push(article)
      return
    }
    const insertAt = list.findIndex(
      (item) =>
        item.sortIndex === undefined ||
        item.sortIndex === null ||
        item.sortIndex > article.sortIndex!
    )
    if (insertAt === -1) list.push(article)
    else list.splice(insertAt, 0, article)
  }

  const handleBackToPicking = async (a: OrderArticle) => {
    a.updatedAt = new Date()
    setDisablePickingButtons(true)

    if (isUnknownArticle(a)) {
      await backToPickingUnknownPtArticle(a)
      setDisablePickingButtons(false)
      return
    }

    const deltaQuantityBeforeBackToPick =
      a.articleState === ARTICLE_STATES.WITH_ISSUE ? 0 : a.locationInfo?.deltaQuantity ?? 0

    const foundPickedIdx = articlesPicked.findIndex((picked) => {
      return (
        picked.ptOrderArticleId! === a.ptOrderArticleId! &&
        a.locationInfo?.name === picked.locationInfo?.name
      )
    })

    let orderResponseFromChangeArticleProp: OrderResponseDto | undefined
    if (foundPickedIdx !== -1) {
      if (articlesPicked[foundPickedIdx].articleState === ARTICLE_STATES.PICKED) {
        const changes: ChangeArticlePropsWrapper = {
          toPick: {
            ptOrderArticleId: a.ptOrderArticleId!,
            mkmProductId: a.idProduct,
            orderId: a.ptOrderId,
            ptArticleId: a.ptArticleId!,
            location: a.locationInfo,
            cardmarketOrderArticleId: a.ptArticleId!,
          },
          maxPickingQuantity: a.isPlayset
            ? a.quantity * getPlaysetCoefficient(a.card.idGame!)
            : a.quantity,
        }

        try {
          orderResponseFromChangeArticleProp = await changeArticleProp(
            a.ptOrderId,
            a.ptOrderArticleId!,
            changes
          )
          handleUpdateOrders([
            convertOrderResponseDtoToOrderClass(orderResponseFromChangeArticleProp),
          ])
        } catch {
          setDisablePickingButtons(false)
          return enqueueSnackbar(t('orders.picking.unpickInvalidOrderStatus'), { variant: 'error' })
        }
      } else if (articlesPicked[foundPickedIdx].articleState === ARTICLE_STATES.WITH_ISSUE) {
        a.issues = []

        const changes: ChangeArticlePropsWrapper = {
          toPick: {
            ptOrderArticleId: a.ptOrderArticleId!,
            mkmProductId: a.idProduct,
            orderId: a.ptOrderId,
            ptArticleId: a.ptArticleId!,
            location: a.locationInfo,
            cardmarketOrderArticleId: a.ptArticleId!,
          },
          maxPickingQuantity: a.isPlayset
            ? a.quantity * getPlaysetCoefficient(a.card.idGame!)
            : a.quantity,
        }

        try {
          orderResponseFromChangeArticleProp = await changeArticleProp(
            a.ptOrderId,
            a.ptOrderArticleId!,
            changes
          )
          handleUpdateOrders([
            convertOrderResponseDtoToOrderClass(orderResponseFromChangeArticleProp),
          ])
        } catch {
          setDisablePickingButtons(false)
          return enqueueSnackbar(t('orders.picking.unpickInvalidOrderStatus'), { variant: 'error' })
        }
      }

      // Bail out BEFORE mutating the store arrays: when the article state is neither PICKED nor
      // WITH_ISSUE no changeArticleProp call runs, so orderResponseFromChangeArticleProp stays
      // undefined. Returning after the splice/push would leave articlesPicked/articlesToPick
      // mutated without the matching state commit below, desyncing the UI.
      if (!orderResponseFromChangeArticleProp) {
        setDisablePickingButtons(false)
        return
      }
      const updatedArticle = orderResponseFromChangeArticleProp.articles.find(
        (searchingArticle: any) => {
          return searchingArticle.ptOrderArticleId === a.ptOrderArticleId!
        }
      )
      if (!updatedArticle) {
        setDisablePickingButtons(false)
        return
      }
      const updatedLocations = updatedArticle.locations

      articlesPicked.splice(foundPickedIdx, 1)
      a.articleState = ARTICLE_STATES.TO_PICK
      insertToPickBySortIndex(articlesToPick, a)

      const [newArticlesToPick, newPickedArticles] =
        updateReservedQuantityInBothListsAccordingLastOrderChanges(
          updatedLocations,
          true,
          articlesToPick,
          articlesPicked,
          a.ptArticleId!
        )

      const [listOfArticlesToPick, listOfPickedArticles] = updatePickedQuantityInDestinationList(
        newArticlesToPick,
        newPickedArticles,
        a.pickedQuantity - Math.abs(deltaQuantityBeforeBackToPick),
        a.ptOrderArticleId!
      )

      setArticlesToPick(listOfArticlesToPick)
      setArticlesPicked(listOfPickedArticles)
    }
    setDisablePickingButtons(false)
    setLastPicked(undefined)
  }

  ////////////// ISSUES

  const handleReportIssue = (a: OrderArticle) => {
    if (openLocationsDialog) setOpenLocationsDialog(false)
    setSelectedArticle(a)
    setOpenReportIssueDialog(true)
  }

  const handleIssues = (a: OrderArticle) => {
    setSelectedArticle(a)
    setOpenReportIssueDialog(true)
  }

  const handleCloseReportIssueDialog = () => {
    setSelectedArticle(undefined)
    setOpenReportIssueDialog(false)
  }

  const handleConfirmReportIssueDialog = async (issue: string, issueCode: string) => {
    if (selectedArticle) {
      selectedArticle.updatedAt = new Date()
      const newIssues = [...(selectedArticle.issues || [])]
      newIssues.push({
        description: issue,
        solved: false,
        solution: '',
        code: issueCode,
      })

      const unknownPtArticle = isUnknownArticle(selectedArticle)

      const maxPickingQuantity = selectedArticle.isPlayset
        ? selectedArticle.quantity * getPlaysetCoefficient(selectedArticle.card.idGame!)
        : selectedArticle.quantity

      // The issue is recorded against this row's location, so send that row's own quantity: it is
      // the quantity the picking list asks from this location, which is what the user is declaring
      // missing. Some rows reach here without one, and the backend then falls back to the whole
      // ordered quantity — more than the location holds as soon as the article is spread over
      // several of them, which the backend rejects. Fall back to the location's own availability.
      const locationForIssue = selectedArticle.locationInfo
        ? {
            ...selectedArticle.locationInfo,
            deltaQuantity:
              Math.abs(selectedArticle.locationInfo.deltaQuantity ?? 0) ||
              Math.min(
                getAvailableQuantityNetOfPickedAndWithIssues(
                  getLocationWithFreshQuantities(selectedArticle)
                ),
                maxPickingQuantity
              ),
          }
        : null

      const changes: ChangeArticlePropsWrapper = {
        articleIssue: {
          ptOrderArticleId: selectedArticle.ptOrderArticleId!,
          mkmProductId: selectedArticle.idProduct,
          orderId: selectedArticle.ptOrderId,
          description: newIssues[0].description,
          issueCode: newIssues[0].code!,
          ptArticleId: unknownPtArticle ? -1 : selectedArticle.ptArticleId!,
          cardmarketOrderArticleId: selectedArticle.ptArticleId!,
          location: locationForIssue,
        },
        maxPickingQuantity,
      }

      try {
        const orderResponseFromChangeArticleProp = await changeArticleProp(
          selectedArticle.ptOrderId,
          selectedArticle.ptOrderArticleId!,
          changes
        )

        selectedArticle.issues = newIssues // update article issues only if article have been updated in DB
        handleUpdateOrders([
          convertOrderResponseDtoToOrderClass(orderResponseFromChangeArticleProp),
        ])
      } catch (error: any) {
        setDisablePickingButtons(false)
        // Leaving the dialog open made a rejected report look like nothing had happened, with the
        // user confirming again and again on a request the backend keeps refusing.
        handleCloseReportIssueDialog()

        const reason = error?.response?.data?.error?.message
        if (reason) console.error(`Report issue rejected by the backend: ${reason}`)

        return enqueueSnackbar(t('orders.picking.createArticleIssueInvalidOrderStatus'), {
          variant: 'error',
        })
      }

      const copyOfToPick = _.cloneDeep(articlesToPick)
      let idx = -1

      if (unknownPtArticle) {
        // don't use the location name: for an unknown article there's no location object at all
        idx = copyOfToPick.findIndex((a) => {
          return a.ptOrderArticleId! === selectedArticle.ptOrderArticleId!
        })
      } else {
        idx = copyOfToPick.findIndex((a) => {
          return (
            a.ptOrderArticleId! === selectedArticle.ptOrderArticleId! &&
            a.locationInfo?.name === selectedArticle.locationInfo?.name
          )
        })
      }
      if (idx !== -1) {
        copyOfToPick.splice(idx, 1)
      }
      setArticlesToPick(copyOfToPick)

      selectedArticle.articleState = ARTICLE_STATES.WITH_ISSUE
      // Intentional no-op: deltaQuantity must be preserved so the article can be
      // re-picked after a back-to-picking. WITH_ISSUE state is already neutralized
      // where it matters via the `articleState === WITH_ISSUE ? 0 : deltaQuantity`
      // check (see backToPickingFromPickedArea).
      // if (selectedArticle.locationInfo) {
      //   selectedArticle.locationInfo.deltaQuantity = 0
      // }
      const copyOfPicked = _.cloneDeep(articlesPicked)
      copyOfPicked.push(selectedArticle)
      setArticlesPicked(copyOfPicked)
    }
    handleCloseReportIssueDialog()
  }

  return (
    <>
      {openLocationsDialog && (
        <LocationsDialog
          open={openLocationsDialog}
          setOpen={() => setOpenLocationsDialog(false)}
          orderArticle={currentOrderToPick.article!}
          quantity={currentOrderToPick.quantityToPickInOrder!}
          handleChoosePickingLocation={handleChoosePickingLocation}
          readonly={openLocationDialogInReadOnlyMode}
          handleReportIssue={handleReportIssue}
          pickedArticles={getMapOfOrderArticlesByPtOrderArticleId(articlesPicked)}
        />
      )}

      {openLocationsErrorDialog && (
        <LocationsErrorDialog
          open={openLocationsErrorDialog}
          setOpen={() => setOpenLocationsErrorDialog(false)}
        />
      )}

      <div className="mb-3">
        <div className="d-flex flex-row">
          <h4 className="mb-2 me-2">{t('orders.picking.title')}</h4>
          <h4 className="primary-color">List {activePickingList?.name}</h4>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Button variant="outlined" color="primary" onClick={handleChooseAnotherList}>
            {t('orders.picking.chooseAnotherList')}
          </Button>
          {/* "Close list" button removed: disabled={articlesToPick.length >= 0} was always true, and the
              picking list closes automatically once everything is picked. */}
          <Button
            onClick={() => callGetArticlesToPick()}
            variant="outlined"
            data-testid="get-articles-to-pick"
          >
            <i className="fa-regular pe-2 fa-arrows-rotate" style={{ fontSize: '18px' }}></i>
            {t('orders.picking.reloadPickingList')}
          </Button>
          <Button onClick={openExportOrdersDialog} variant="outlined">
            <i className="fa-solid pe-2 fa-download" style={{ fontSize: '18px' }}></i>
            Export
          </Button>
        </div>
      </div>

      <div className="mb-3" data-testid={'filters'}>
        <Typography color="primary" className="mb-2">
          {t('orders.view.title')}
        </Typography>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Button
            variant={view === 'list' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setView('list')}
          >
            <i className="fa-regular fa-list-ul pe-2" style={{ fontSize: '18px' }}></i>
            {t('orders.view.list')}
          </Button>
          <Button
            variant={view === 'images-vertical' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setView('images-vertical')}
          >
            <i className="fa-solid fa-grip-vertical pe-2" style={{ fontSize: '18px' }}></i>
            IMAGES VERTICAL
          </Button>
          <Button
            variant={view === 'images-horizontal' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setView('images-horizontal')}
          >
            <i className="fa-solid fa-grip pe-2" style={{ fontSize: '18px' }}></i>
            IMAGES HORIZONTAL
          </Button>
          <Button
            variant={view === 'singleImage' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setView('singleImage')}
          >
            <i className="fa-solid fa-image pe-2" style={{ fontSize: '18px' }}></i>
            {t('orders.view.singleImage')}
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Typography color="primary" className="mb-2">
          {t('orders.sortBy')}
        </Typography>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <ArticleSorter />
        </div>
      </div>

      <div className="d-flex align-items-center mb-4" data-testid={'smart-locations'}>
        <div className="p-1 me-2">
          <Typography color="primary">Smart locations</Typography>
        </div>
        <CustomTooltip title={smartLocationsTooltipTitle}>
          <Switch
            name="smart location"
            checked={activatedSmartLocation}
            onChange={handleChangeSmartLocation}
            data-testid={'toggle-switch-smart-locations'}
          />
        </CustomTooltip>
      </div>

      {loading ? (
        <div className="w-100 d-flex flex-column align-items-center ">
          <h6 className="pb-5">Loading</h6>
          <CircularProgress size={100} />
        </div>
      ) : (
        <>
          {view === 'list' && (
            <>
              <div className="w-100 d-flex flex-row justify-content-between">
                <h5 className="fw-bold">To be picked</h5>
                <div data-testid="orders-and-unique-articles">
                  {new Set(articlesToPick.map((article) => article.ptOrderId)).size} orders,{' '}
                  {new Set(articlesToPick.map((article) => article.ptArticleId)).size} unique
                  articles
                </div>
              </div>
              {articlesToPick.length > 0 ? (
                <PickingTable
                  type="toPick"
                  handlePick={handlePick}
                  handleCompletePicking={handleCompletePicking}
                  handleReportIssue={handleReportIssue}
                />
              ) : (
                <Typography>No articles here to pick</Typography>
              )}
              <Box padding={1} />

              <div className="mt-5">
                <h5 className="fw-bold">Picked</h5>
                {articlesPicked.length > 0 ? (
                  <PickingTable
                    type="picked"
                    handleIssues={handleIssues}
                    handleCompletePicking={handleCompletePicking}
                    handleBackToPicking={
                      activePickingList?.state === 'open' ? handleBackToPicking : undefined
                    }
                  />
                ) : (
                  <Typography>No articles here</Typography>
                )}
              </div>
              <Box padding={1} />
            </>
          )}

          {view === 'images-vertical' && (
            <>
              <h5 className="fw-bold">To be picked</h5>
              <PickingImageDisplay
                type="toPick"
                horizontalView={false}
                handlePick={handlePick}
                handleCompletePicking={handleCompletePicking}
                handleReportIssue={handleReportIssue}
              />

              <div className="mt-5">
                <h5 className="fw-bold">Picked</h5>
                <PickingImageDisplay
                  type="picked"
                  horizontalView={false}
                  handleIssues={handleIssues}
                  handleCompletePicking={handleCompletePicking}
                  handleBackToPicking={handleBackToPicking}
                />
              </div>
            </>
          )}

          {view === 'images-horizontal' && (
            <>
              <h5 className="fw-bold">To be picked</h5>
              <PickingImageDisplay
                type="toPick"
                horizontalView={true}
                handlePick={handlePick}
                handleCompletePicking={handleCompletePicking}
                handleReportIssue={handleReportIssue}
              />
              {/* </Box>*/}

              <div className="mt-5">
                <h5 className="fw-bold">Picked</h5>
                <PickingImageDisplay
                  type="picked"
                  horizontalView={true}
                  handleIssues={handleIssues}
                  handleCompletePicking={handleCompletePicking}
                  handleBackToPicking={handleBackToPicking}
                />
              </div>
            </>
          )}

          {view === 'singleImage' &&
            (articlesToPick.length > 0 ? (
              <SingleImagePicking
                article={articlesToPick[currentArticleIdx]}
                handlePick={handlePick}
                handleCompletePicking={handleCompletePicking}
                handleReportIssue={handleReportIssue}
              />
            ) : (
              <div>
                <h6>No more articles to pick</h6>
              </div>
            ))}
        </>
      )}
      <ReportIssueDialog
        open={openReportIssueDialog}
        onClose={handleCloseReportIssueDialog}
        onConfirm={handleConfirmReportIssueDialog}
        type="article"
        order={undefined}
      />

      <ExportDialog
        title={t('csv.export')}
        content={
          <ExportPickingList
            articlesToPick={articlesToPick}
            articlesPicked={articlesPicked}
            pickingListId={activePickingList!.ptId}
          />
        }
      />
    </>
  )
}
